"use client";

import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import type { RuntimeRouteContext } from "../context/execution-context";
import type { ResolvedScreen } from "./index-types";
import type { StudioScreenRendererProps } from "./StudioScreenRenderer.props";
import {
 extractLocalePrefix,
 pickLocaleRecord,
 resolvePageLocale,
} from "./StudioScreenRenderer.utils.locale-route";
import {
 deriveRouteParamsForScreen,
 deriveShellHeaderForScreen,
 extractSearch,
 parseSearchParams,
 resolvePathnameLike,
 resolveScreenForPath,
} from "./StudioScreenRenderer.utils";

const BROWSER_NAVIGATION_EVENT = "studio-runtime:navigate";

function getBrowserLocationSnapshot(): string {
 if (typeof window === "undefined") return "";
 return `${window.location.pathname}${window.location.search}`;
}

function subscribeToBrowserNavigation(onChange: () => void): () => void {
 if (typeof window === "undefined") return () => {};

 window.addEventListener("popstate", onChange);
 window.addEventListener(BROWSER_NAVIGATION_EVENT, onChange);
 return () => {
 window.removeEventListener("popstate", onChange);
 window.removeEventListener(BROWSER_NAVIGATION_EVENT, onChange);
 };
}

export function useScreenRoute(props: StudioScreenRendererProps): {
 screen: ResolvedScreen | null;
 route: RuntimeRouteContext;
 translations: Record<string, unknown> | undefined;
 browserLocation: string;
 isHistoryNavigation: boolean;
 locale: string | undefined;
 requestLocale: string | undefined;
} {
 const {
 app,
 screenId,
 screen: providedScreen,
 slug,
 pathname,
 initialPathname: providedInitialPathname,
 currentPathname: providedCurrentPathname,
 search,
 params,
 routeParams,
 activeLocale,
 } = props;
 const locales = app.i18n?.locales ?? [];
 const browserLocation = useSyncExternalStore(
 subscribeToBrowserNavigation,
 getBrowserLocationSnapshot,
 () => "",
 );
 const [poppedLocation, setPoppedLocation] = useState<string | null>(null);
 useEffect(() => {
 const onPopState = () => setPoppedLocation(getBrowserLocationSnapshot());
 window.addEventListener("popstate", onPopState);
 return () => window.removeEventListener("popstate", onPopState);
 }, []);

 const isHistoryNavigation =
 poppedLocation !== null && browserLocation === poppedLocation;
 const hasCustomNavigate = Boolean(props.navigate);
 const providedPath = providedCurrentPathname ?? pathname ?? providedInitialPathname;
 const hasBrowserLocation = browserLocation !== "" &&
 (!hasCustomNavigate || isHistoryNavigation);
 const initialPathname = resolvePathnameLike(providedPath ?? "/");
 const currentPathname = resolvePathnameLike(
 hasBrowserLocation ? browserLocation : providedPath ?? "/",
 );

 const initialLocalePrefix = extractLocalePrefix(initialPathname, locales);

 const localeSource = hasBrowserLocation
 ? currentPathname
 : providedInitialPathname ?? currentPathname;
 const urlLocalePrefix = extractLocalePrefix(localeSource, locales);
 const urlLocale = resolvePageLocale(
 localeSource,
 locales,
 Object.keys(app.translationsByLocale ?? {}),
 );

 const activeLocaleFromHost =
 typeof activeLocale === "string" && activeLocale.trim()
 ? activeLocale.trim()
 : undefined;
 const requestLocale = urlLocale ?? activeLocaleFromHost;

 const lenientPrefix = urlLocale && !urlLocalePrefix ? `/${urlLocale}` : null;
 const stripLenientPrefix = (path: string): string =>
 lenientPrefix && path.toLowerCase().startsWith(`${lenientPrefix}/`)
 ? path.slice(lenientPrefix.length) || "/"
 : path;
 const screenPathname =
 urlLocalePrefix?.strippedPath ?? stripLenientPrefix(currentPathname);
 const screenInitialPathname =
 initialLocalePrefix?.strippedPath ?? stripLenientPrefix(initialPathname);
 const hasChangedBrowserPath = hasBrowserLocation && screenPathname !== screenInitialPathname;
 const pathWithSearch = pathname ?? initialPathname;
 const currentSearch = hasBrowserLocation
 ? extractSearch(browserLocation)
 : search ?? (
 extractSearch(providedCurrentPathname) ||
 (pathWithSearch && resolvePathnameLike(pathWithSearch) === currentPathname
 ? extractSearch(pathWithSearch) : "")
 );

 const screen = useMemo<ResolvedScreen | null>(() => {
 const routeScreen = resolveScreenForPath(app, screenPathname);
 if (hasBrowserLocation) {
 if (routeScreen) return routeScreen;
 if (hasChangedBrowserPath) return null;
 return screenId ? app.screens[screenId] ?? null : providedScreen ?? null;
 }
 if (screenId) return app.screens[screenId] ?? null;
 return routeScreen ?? providedScreen ?? null;
 }, [app, screenId, providedScreen, screenPathname, hasBrowserLocation, hasChangedBrowserPath]);
 const routeSlug = slug ?? screen?.manifest.slug ?? "";
 const resolvedRouteParams = useMemo(
 () => (!hasChangedBrowserPath ? params ?? routeParams : undefined) ??
 deriveRouteParamsForScreen(app, routeSlug, screenPathname),
 [params, routeParams, hasChangedBrowserPath, app, routeSlug, screenPathname],
 );
 const query = useMemo(() => parseSearchParams(currentSearch), [currentSearch]);
 const route = useMemo<RuntimeRouteContext>(
 () => ({
 slug: routeSlug,
 params: { ...resolvedRouteParams },
 query,
 ...(screen ? { shell: deriveShellHeaderForScreen(screen) } : {}),
 }),
 [routeSlug, resolvedRouteParams, query, screen],
 );

 const translations =
 pickLocaleRecord(app.translationsByLocale, requestLocale) ??
 pickLocaleRecord(app.translationsByLocale, activeLocale) ??
 app.translations;
 return {
 screen,
 route,
 translations,
 browserLocation,
 isHistoryNavigation,
 locale: urlLocale,
 requestLocale,
 };
}